import React, { useState } from "react";
import Modal from "react-modal";
import { useDropzone } from "react-dropzone";
import "cropperjs/dist/cropper.css";
import axios from "axios";
import ProgressBar from "components/ProgressBar";
import { Img } from "components/Img";
import { showGlobalModal } from "components/GlobalModal";

Modal.setAppElement("#root");

const FOTOR_ACCESS_KEY = "sk_wK3iRjOwNEosEZ2QcLgCQIpe";

function getFileNameFromUrl(url) {
    return url ? url.substring(url.lastIndexOf('/') + 1) : "Unknown file";
}

const FotorImageUpload: React.FC<{
    onChange: (file: object) => void;
    imageValue: {
        originalImage: string;
        selectedImage: string;
        enhancedImage: string;
    };
    label?: string;
    customMessage?: string;
    wantHDOption?: boolean;
    previewNotRequired?: boolean;
    disable?: boolean;
    labelClassNames?: string;
    containerHeight?: string | any;
    maxSize?: number;
    isError?: boolean;
}> = ({
    onChange,
    imageValue,
    label,
    customMessage,
    wantHDOption = true,
    disable,
    maxSize = 5,
    isError
}) => {
        const [modalIsOpen, setModalIsOpen] = useState(false);
        const [showLowResModal, setShowLowResModal] = useState(false);
        const [loader, setLoader] = useState(false);
        const [enhancementStatus, setEnhancementStatus] = useState<{
            processing: boolean;
            taskId?: string;
        }>({ processing: false });
        const [uploadController, setUploadController] = useState<AbortController | null>(null);

        const IS_ORIGINAL_IMAGE = Boolean(
            imageValue?.selectedImage === imageValue?.originalImage
        );
        const IS_ENHANCED_IMAGE = Boolean(
            imageValue?.selectedImage === imageValue?.enhancedImage
        );

        const handleImageUpload = async (file: File) => {
            // Create a new AbortController
            const controller = new AbortController();
            setUploadController(controller);

            setLoader(true);
            const formData = new FormData();
            formData.append("files", file);

            try {
                const uploadResponse = await axios.post(
                    "https://dev.soomuch.ai/api/v1/master/upload/tempfiles",
                    formData,
                    {
                        headers: { "Custom-Header": "multipart/form-data" },
                        signal: controller.signal
                    }
                );

                const imageUrl = uploadResponse?.data?.data?.[0];
                onChange({
                    originalImage: imageUrl,
                    selectedImage: imageUrl,
                    enhancedImage: "",
                });
                setModalIsOpen(true);

                // Auto-enhance after upload is optional
                // handleEnhanceImage(imageUrl);
            } catch (error: any) {
                if (axios.isCancel(error)) {
                    console.log("Upload aborted:", error.message);
                } else {
                    console.error("Upload error:", error);
                    showGlobalModal(error?.response?.data?.message || "Upload failed!", "error");
                }
            } finally {
                setLoader(false);
            }
        };

        // Function to cancel the request
        const handleCancelUpload = () => {
            if (uploadController) {
                uploadController.abort();
                setUploadController(null);
                setLoader(false);
            }
        };

        const handleEnhanceImage = async () => {
            if (!imageValue?.originalImage) return;

            try {
                setEnhancementStatus({ processing: true, taskId: "" });
                console.log("[ENHANCE] Starting enhancement for:", imageValue.originalImage);

                const response = await axios.post(
                    "https://dev.soomuch.ai/api/v1/master/fr/imageEnhance",
                    {
                        user_image_url: imageValue.originalImage,
                        face_upsample: true,
                        background_enhance: true,
                        upscale: 2,
                        fidelity_weight: 0.5,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${FOTOR_ACCESS_KEY}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const taskId = response.data?.data;
                if (!taskId) throw new Error("No task ID returned from enhancement API");

                console.log("[ENHANCE] Enhancement task submitted. Task ID:", taskId);
                setEnhancementStatus({ processing: true, taskId });

                startPollingEnhancementStatus(taskId);
            } catch (error: any) {
                console.error("[ENHANCE ERROR]", error);
                showGlobalModal(error?.response?.data?.message || "Unknown error occurred", "error");
                setEnhancementStatus({ processing: false, taskId: "" });
            }
        };

        const startPollingEnhancementStatus = (taskId: string) => {
            console.log("[POLLING] Started for Task ID:", taskId);
            let attempts = 0;
            const maxAttempts = 20;

            const intervalId = setInterval(async () => {
                try {
                    console.log(`[POLLING] Attempt #${attempts + 1}`);
                    const res = await axios.get(`https://api-b.fotor.com/v1/aiart/tasks/${taskId}`, {
                        headers: { Authorization: `Bearer ${FOTOR_ACCESS_KEY}` },
                    });

                    const data = res.data?.data;
                    console.log("[POLLING] Response data:", data);

                    // Status code definitions:
                    // 0: in progress, 1: completed, 2: failed
                    const status = data?.status;
                    const resultUrl = data?.resultUrl;

                    if (status === 1 && resultUrl) {
                        clearInterval(intervalId);
                        console.log("[POLLING] Enhancement completed:", resultUrl);

                        // Update the state with the enhanced image URL
                        onChange({
                            originalImage: imageValue.originalImage,
                            selectedImage: imageValue.selectedImage, // Keep current selection
                            enhancedImage: resultUrl
                        });
                        setEnhancementStatus({ processing: false, taskId: "" });
                    } else if (status === 2) {
                        clearInterval(intervalId);
                        console.error("[POLLING] Enhancement failed.");
                        showGlobalModal("Fotor task could not complete successfully", "error");
                        setEnhancementStatus({ processing: false, taskId: "" });
                    } else {
                        console.log(`[POLLING] Status: ${status}. Still processing...`);
                    }

                    if (++attempts >= maxAttempts) {
                        clearInterval(intervalId);
                        console.warn("[POLLING] Max attempts reached without success.");
                        setEnhancementStatus({ processing: false, taskId: "" });
                        showGlobalModal("It's taking longer than expected. Please try again later.", "warning");
                    }
                } catch (err) {
                    clearInterval(intervalId);
                    console.error("[POLLING ERROR]", err);
                    setEnhancementStatus({ processing: false, taskId: "" });
                    showGlobalModal(err?.response?.data?.message || "Polling failed unexpectedly", "error");
                }
            }, 3000); // every 3 seconds
        };

        const handleSelection = (url) => {
            onChange({
                ...imageValue,
                selectedImage: url,
            });
        };

        const handleModalCancel = () => {
            setModalIsOpen(false);
        };

        const handleSaveEnhancedImage = () => {
            setModalIsOpen(false);
        };

        const handleDeleteTheUploadedFile = () => {
            onChange({
                originalImage: "",
                selectedImage: "",
                enhancedImage: "",
            });
        };

        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop: files => {
                if (files[0]?.size > maxSize * 1024 * 1024) {
                    showGlobalModal(`File exceeds ${maxSize}MB`, "error");
                    return;
                }
                files[0] && handleImageUpload(files[0]);
            },
            accept: { "image/*": [] },
            multiple: false,
            disabled: disable || loader
        });

        React.useEffect(() => {
            if (imageValue?.originalImage) {
                setShowLowResModal(true);
            }
        }, [imageValue?.originalImage]);


        return (
            <div className="w-full space-y-2">
                <div
                    className={`
                    relative 
                    flex 
                    flex-col 
                    items-center 
                    justify-center
                    p-2
                    h-[7vh]
                    border 
                    border-solid 
                    ${isError ? "border-red-500" : "border-gray-300"}
                    rounded-md 
                    bg-[white]
                    overflow-hidden 
                    ${isDragActive ? " bg-blue-100" : ""}
                    `}
                >
                    <input {...getInputProps()} />
                    <div className="w-full">
                        {!imageValue?.originalImage ? (
                            <div className="flex flex-col justify-center items-center gap-y-2">
                                <div className="flex justify-between items-center gap-4 w-full">
                                    {loader ? (
                                        <ProgressBar />
                                    ) : (
                                        !!label && (
                                            <label className="block text-[2.5vh] font-medium text-gray-700">
                                                {label}
                                                <span className="text-[1.3vh]">{customMessage || ` JPG, JPEG (Max ${maxSize}MB)`}</span>
                                            </label>
                                        )
                                    )}
                                    {loader ? (
                                        <button onClick={handleCancelUpload} type="button">
                                            <Img
                                                src="/images/new-landing-page/blackCloseButton.svg"
                                                alt="blackCloseButton"
                                                className="object-contain"
                                            />
                                        </button>
                                    ) : (
                                        !disable ? <button
                                            {...getRootProps()}
                                            type="button"
                                            className="py-2 px-4 bg-blue-700 text-white rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 text-[1.9vh] font-medium"
                                            style={{ color: '#fff' }}
                                        >
                                            Upload
                                        </button> : <></>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-[1.9vh] flex justify-between items-center gap-x-4">
                                <div className="flex gap-x-4 items-center">
                                    <Img
                                        src={imageValue?.selectedImage}
                                        alt="image"
                                        className="min-w-[4vh] min-h-[4vh] max-w-[4vh] max-h-[4vh] object-contain rounded-full"
                                    />
                                    <label className="block text-[2vh] font-medium text-gray-700">
                                        {getFileNameFromUrl(imageValue?.selectedImage)}
                                    </label>
                                </div>
                               {!disable && <div className="flex items-center gap-x-4">
                                    {wantHDOption && (
                                        <button
                                            className="bg-[#5E4DE0] rounded-full p-1"
                                            onClick={e => {
                                                e.stopPropagation();
                                                if (!disable && !loader) {
                                                    setModalIsOpen(true);
                                                }
                                            }}
                                            type="button"
                                        >
                                            <Img
                                                src="/images/new-landing-page/descriptionImage.svg"
                                                alt="enhanceImage"
                                                className="w-[3.4vh] h-[3.4vh] object-contain"
                                            />
                                        </button>
                                    )}
                                    <button
                                        disabled={disable}
                                        onClick={handleDeleteTheUploadedFile}
                                        type="button"
                                    >
                                        <Img
                                            src="/images/new-landing-page/delete.svg"
                                            alt="delete"
                                            className="w-[3.4vh] h-[3.4vh] object-contain"
                                        />
                                    </button>
                                </div>}
                            </div>
                        )}
                    </div>
                </div>

                {wantHDOption && (
                    <Modal
                        isOpen={modalIsOpen}
                        onRequestClose={handleModalCancel}
                        contentLabel="Enhance Image"
                        className="fixed inset-0 flex items-center justify-center z-40 font-manrope backdrop-blur-sm"
                        overlayClassName="fixed inset-0 bg-black/90 z-[1001] transition-opacity duration-300"
                        closeTimeoutMS={300}
                    >
                        <div className="bg-[white] p-6 rounded-xl w-[60%] max-w-3xl shadow-2xl border border-white/10">
                            <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
                                {/* Original Image Section */}
                                <div className="group relative">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                        Original Image
                                        {IS_ORIGINAL_IMAGE && (
                                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 py-1 px-2 rounded-full">Selected</span>
                                        )}
                                    </h3>
                                    <div className={`relative aspect-auto rounded-xl overflow-hidden transition-all duration-300 ${IS_ORIGINAL_IMAGE
                                        ? "border-4 border-blue-500 shadow-lg shadow-blue-200"
                                        : "border-2 border-gray-100 bg-gray-50"
                                        }`}>
                                        {imageValue?.originalImage && (
                                            <div className="relative">
                                                <img
                                                    src={imageValue.originalImage}
                                                    alt="Original"
                                                    className={`w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105`}
                                                    onClick={() => handleSelection(imageValue.originalImage)}
                                                />
                                                {/* {IS_ORIGINAL_IMAGE && (
                                                    <div className="absolute top-3 right-3 bg-blue-500 rounded-full p-1 shadow-md">
                                                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                )} */}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Enhanced Image Section */}
                                <div className="group relative">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                        Enhanced Image
                                        {IS_ENHANCED_IMAGE && imageValue?.enhancedImage && (
                                            <span className="ml-2 text-xs bg-purple-100 text-purple-800 py-1 px-2 rounded-full">Selected</span>
                                        )}
                                    </h3>
                                    <div className={`relative aspect-auto rounded-xl overflow-hidden transition-all duration-300 ${IS_ENHANCED_IMAGE && imageValue?.enhancedImage
                                        ? "border-4 border-purple-500 shadow-lg shadow-purple-200"
                                        : "border-2 border-gray-100 bg-gradient-to-br from-gray-50 to-gray-100"
                                        }`}>
                                        {imageValue?.enhancedImage ? (
                                            <div className="relative">
                                                <img
                                                    src={imageValue.enhancedImage}
                                                    alt="Enhanced"
                                                    className={`w-full h-full object-cover cursor-pointer transition-transform duration-300 hover:scale-105`}
                                                    onClick={() => handleSelection(imageValue.enhancedImage)}
                                                />
                                                {/* {IS_ENHANCED_IMAGE && (
                                                    <div className="absolute top-3 right-3 bg-purple-500 rounded-full p-1 shadow-md">
                                                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                )} */}
                                            </div>
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center p-4 relative">
                                                <div className="flex flex-col items-center justify-center gap-4">
                                                    <button
                                                        type="button"
                                                        className={`transition-transform ${enhancementStatus.processing ? "cursor-not-allowed" : "hover:scale-110"
                                                            }`}
                                                        onClick={(e) => !enhancementStatus.processing && handleEnhanceImage()}
                                                        disabled={enhancementStatus.processing}
                                                    >
                                                        {enhancementStatus.processing ? (
                                                            <div className=" p-4 ">
                                                                <img src="/images/loader.gif" className="w-8 h-8" alt="Processing" />
                                                            </div>
                                                        ) : (
                                                            <div className="bg-purple-300 p-4 rounded-full shadow-lg hover:bg-purple-600">
                                                                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M13 10V3L4 14h7v7l9-11h-7z"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </button>

                                                    <p className="text-center text-base text-gray-600 font-small max-w-xs">
                                                        {enhancementStatus.processing
                                                            ? "We're enhancing your image. Please wait a moment..."
                                                            : "Your image is low resolution. Enhance it !"}
                                                    </p>
                                                </div>
                                            </div>

                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={handleModalCancel}
                                    className="px-5 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveEnhancedImage}
                                    style={{ color: '#fff' }}
                                    className="px-5 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-purple-600 transition-colors shadow-sm hover:shadow-md"
                                >
                                    Save Image
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        );
    };

export default FotorImageUpload;